import { CARD_TYPE } from '@shared/config';

import { normalizeEnumKey } from './normalizeEnumKey';

/**
 * Normalizes one debit card of a backend payload to the app's config keys.
 *
 * The backend spells card enums inconsistently — cardStatus arrives as 'active' from
 * DiPocket but 'Canceled' / 'ConsentPending' from Swan, and 'Frozen' is synthesized from
 * the isFrozen flag — so every card goes through normalizeEnumKey at the API boundary,
 * the one place allowed to normalize.
 *
 * Shared rather than per-endpoint because the same card shape reaches the app from
 * /v1/user (the cardholder's own cards) and from /v1/user/:userId (an employer reading
 * an employee), and a card normalized in only one of them silently misses every config
 * lookup keyed camelCase.
 *
 * @param {object | null | undefined} debitCard - Raw card from the backend
 * @returns {object | null} Normalized card, or null when there is none
 */
export const normalizeDebitCard = (debitCard) => {
	if (!debitCard) return null;
	const type = normalizeEnumKey(debitCard.type);
	return {
		...debitCard,
		// 'VirtualAndPhysical' is a plastic card ordered together with its virtual
		// counterpart. It is absent from CARD_TYPE, so left as-is it would fall through as
		// virtual and lose both its PIN button and its type badge. Matched case-insensitively
		// like card-service's isPhysicalCard: legacy rows also spell it 'virtualandphysical'.
		type: type?.toLowerCase().includes(CARD_TYPE.physical) ? CARD_TYPE.physical : type,
		cardStatus: normalizeEnumKey(debitCard.cardStatus),
		// Lifecycle of the plastic, independent of cardStatus: a card converted from
		// virtual keeps cardStatus 'active' and type 'virtual' and reports its plastic
		// only here. Absent on a card that has no plastic.
		physicalCardStatus: normalizeEnumKey(debitCard.physicalCardStatus),
	};
};
